import React, { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, CheckCircle2, XCircle } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';

interface Operator {
  id: string;
  name: string;
  rut: string;
  active: boolean;
  created_at: string;
  created_by: string;
}

export const OperatorManagement = () => {
  const [operators, setOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newOperator, setNewOperator] = useState({ name: '', rut: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    try {
      const { data, error } = await supabase
        .from('operators')
        .select('*')
        .order('name');

      if (error) throw error;
      setOperators(data || []);
    } catch (err) {
      console.error('Error fetching operators:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar operadores');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOperator = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const { data: operatorData, error: operatorError } = await supabase
        .from('operators')
        .insert({
          name: newOperator.name,
          rut: newOperator.rut,
          created_by: 'admin'
        })
        .select()
        .single();

      if (operatorError) throw operatorError;

      setOperators(prev => [...prev, operatorData]);
      setShowAddForm(false);
      setNewOperator({ name: '', rut: '' });
    } catch (err) {
      console.error('Error adding operator:', err);
      setError(err instanceof Error ? err.message : 'Error al agregar operador');
    } finally {
      setIsSubmitting(false);
    }
  };

  const toggleOperatorStatus = async (operatorId: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('operators')
        .update({ active: !currentStatus })
        .eq('id', operatorId);

      if (error) throw error;

      setOperators(prev =>
        prev.map(operator =>
          operator.id === operatorId
            ? { ...operator, active: !currentStatus }
            : operator
        )
      );
    } catch (err) {
      console.error('Error updating operator status:', err);
      setError(err instanceof Error ? err.message : 'Error al actualizar el estado del operador');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#004A98]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Administración</h1>
            <p className="mt-1 text-sm text-gray-500">
              Gestión de usuarios y operadores
            </p>
          </div>
          <div className="flex border border-gray-300 rounded-md p-1">
            <Link
              to="/admin/users"
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-md',
                location.pathname === '/admin/users'
                  ? 'bg-[#004A98] text-white'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Usuarios
            </Link>
            <Link
              to="/admin/operators"
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-md',
                location.pathname === '/admin/operators'
                  ? 'bg-[#004A98] text-white'
                  : 'text-gray-500 hover:text-gray-700'
              )}
            >
              Operadores
            </Link>
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#004A98] hover:bg-[#003875]"
        >
          <Plus className="h-5 w-5 mr-2" />
          Agregar Operador
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertCircle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showAddForm && (
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">Agregar Nuevo Operador</h2>
          <form onSubmit={handleAddOperator} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Nombre Completo
              </label>
              <input
                type="text"
                required
                value={newOperator.name}
                onChange={(e) => setNewOperator({ ...newOperator, name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                RUT
              </label>
              <input
                type="text"
                required
                value={newOperator.rut}
                onChange={(e) => setNewOperator({ ...newOperator, rut: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#004A98] focus:ring-[#004A98] sm:text-sm"
                placeholder="12.345.678-9"
              />
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setNewOperator({ name: '', rut: '' });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#004A98] hover:bg-[#003875] disabled:opacity-50"
              >
                {isSubmitting ? 'Agregando...' : 'Agregar'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nombre
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                RUT
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Fecha de Registro
              </th>
              <th scope="col" className="relative px-6 py-3">
                <span className="sr-only">Acciones</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {operators.map((operator) => (
              <tr key={operator.id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{operator.name}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">{operator.rut}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      operator.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}
                  >
                    {operator.active ? (
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-1" />
                    )}
                    {operator.active ? 'Activo' : 'Inactivo'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {new Date(operator.created_at).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <button
                    onClick={() => toggleOperatorStatus(operator.id, operator.active)}
                    className={`text-sm font-medium ${
                      operator.active
                        ? 'text-red-600 hover:text-red-900'
                        : 'text-green-600 hover:text-green-900'
                    }`}
                  >
                    {operator.active ? 'Desactivar' : 'Activar'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};